import * as Linkify from 'linkifyjs';
import 'linkify-plugin-mention';
import { uniq } from 'lodash';

export const MBChatInputUtil = {
  // Calculates how many users will be notified when an urgent message is sent.
  // If specific people are mentioned, only they are notified; otherwise (or when
  // `@all` is mentioned) everyone else in the channel is notified.
  //
  // The full channel roster is no longer loaded eagerly, so the "everyone" case
  // uses the channel member count rather than the length of a loaded roster, and
  // specific mentions are counted from the message content directly. We never
  // notify the sender, so the "everyone" case subtracts 1 from the member count.
  numUrgentRecipients(content: string, memberCount: number): number {
    // 1. Get all distinct mentions from the content. Linkify is the same
    //    module as we use on the backend.
    const matches = Linkify.find(content);
    const mentions = uniq(
      matches.filter(match => match.type === 'mention').map(match => match.value),
    );

    // 2. `@all` notifies everyone in the channel except the sender.
    if (mentions.includes('@all')) {
      return Math.max(0, memberCount - 1);
    }

    // 3. Specific mentions notify only the mentioned people.
    if (!!mentions.length) {
      return mentions.length;
    }

    // 4. Otherwise everyone in the channel except the sender is notified.
    return Math.max(0, memberCount - 1);
  },

  /*
        This function returns the current @mention, if any.
        In the examples belof "|" is where the cursor is.
        "@fa|nt " => @fant
        "@fant|" => @fant
        "@fant |" => -
        "|@fant" => @fant
        "| @fant" => @fant
        "new line
        |@fant" => @fant
    */
  mentionQuery(string: string, cursorPosition: number): string {
    // 1. Find the string between the cursor and the next space/line break/tab
    // "\s" regex matches spaces, line breaks and tabs
    // https://stackoverflow.com/a/4377521/12338002
    const currentWordStart = string.substr(0, cursorPosition).split(/\s/).reverse()[0]; // get last item
    // 2. Find the string between the cursor and the previous space/line break/tab
    const currentWordEnd = string.substr(cursorPosition).split(/\s/)[0];
    // 3. The current word is the combination of these two
    const currentWord = `${currentWordStart}${currentWordEnd}`;

    // 4. If the word starts with "@", we say that it's a mention
    if (currentWord[0] !== '@') {
      // @ts-expect-error -- strict migration
      return undefined;
    }
    // 5. The returned mention is the word after "@"
    return currentWord.substr(1);
  },

  addMention(
    string: string,
    cursorPosition: number,
    mention: string,
  ): {
    string: string;
    cursorPosition: number;
  } {
    // 1. To insert a mention, we first get all the text
    //    before and after the current mention query.
    //    Eg "hello @fa|nt end" (where "|" is the cursor position)
    //    => before: "hello @fa"
    //    => after:  "nt end"
    const before = string.slice(0, cursorPosition);
    const after = string.slice(cursorPosition);

    // 2. When adding a mention, we assume that the cursor is currently
    //    adjacent to a mention query (eg "@fa"). We want to remove this
    //    mention query so that we only have the raw content before/after.
    //    Eg before: "hello @fa" => "hello "
    //    Eg after:  "nt end" => " end"
    //    The way the regex works is by replacing all non-space symbols (\S*)
    //    either before or after the cursor. To only replace one group of a
    //    regex match, see this answer:
    //    https://stackoverflow.com/a/3954957
    //
    // /(\s|^)(\S*)$/ => (space/tab/newline/startofstring)(non-space)(end)
    // '$1' means we keep the first group in the regex
    // (aka the space/tab/newline/startofstring)
    const beforeWithoutMentionQuery = before.replace(/(\s|^)(\S*)$/, '$1');
    // /^(\S*)(\s|$)/ => (start)(non-space)(space/tab/newline/endofstring)
    // '$2' means we keep the second group in the regex
    // (aka the space/tab/newline/endofstring)
    // We also do another replace to make sure that we get rid of leading spaces
    const afterWithoutMentionQuery = after.replace(/^(\S*)(\s|$)/, '$2').replace(/^[ ]/, '');

    // 3. Between the content before and after we want to have a separator.
    //
    // If there is no content before OR we have space/newline/tab before, we
    // don't need to have a separator before the mention. Otherwise we add a space.
    const beforeSeparator =
      !beforeWithoutMentionQuery || !!beforeWithoutMentionQuery.match(/\s$/) ? '' : ' ';
    // If the string after does not start with a space, we add a space.
    const afterSeparator = !!afterWithoutMentionQuery.match(/^[ ]/) ? '' : ' ';

    // 4. Construct the new string (except for the after part). We use
    //    this string's length to figure out where to put the cursor.
    const stringWithoutAfter = `${beforeWithoutMentionQuery}${beforeSeparator}@${mention}${afterSeparator}`;

    return {
      string: `${stringWithoutAfter}${afterWithoutMentionQuery}`,
      cursorPosition: stringWithoutAfter.length,
    };
  },
};
